import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Logo, Check, X } from '../components/Icons.jsx';
import DrillResultBar from '../components/DrillResultBar.jsx';

const PASSAGES = [
  {
    title: 'The placebo effect',
    text: `In medicine, the placebo effect describes a measurable improvement in a patient's condition that cannot be explained by the active ingredient of a treatment. The effect is most pronounced for symptoms that depend on subjective reporting, such as pain or fatigue, but it has also been observed in conditions with objective markers. Researchers now understand that expectation, conditioning and the broader clinical encounter all contribute. The implication for trial design is significant: any treatment must be shown to outperform a credible placebo before clinical claims can be supported. Some bioethicists argue that, where no proven treatment exists, the controlled use of placebo can be both effective and ethical.`,
    qs: [
      { q: 'The placebo effect has been observed in conditions with objective measurable outcomes.', a: 'True', why: 'The passage states "it has also been observed in conditions with objective markers."' },
      { q: 'Expectation alone is responsible for the placebo effect.', a: 'False', why: '"Expectation, conditioning and the broader clinical encounter all contribute" — multiple factors are named.' },
      { q: 'Any treatment must outperform a credible placebo before clinical claims can be supported.', a: 'True', why: 'Stated directly: "any treatment must be shown to outperform a credible placebo before clinical claims can be supported."' },
      { q: 'All bioethicists agree that controlled placebo use is ethical where no proven treatment exists.', a: 'False', why: '"Some bioethicists argue" — not all. The passage does not say this is a universal view.' },
    ]
  },
  {
    title: 'NHS waiting times',
    text: `Waiting times in the NHS have been the focus of policy debate for decades. Although total funding has increased in real terms, the demand created by an ageing population and the long tail of pandemic-deferred care has outpaced capacity gains. Workforce shortages, particularly among trained nurses and general practitioners, are the most cited cause by trust-level leaders. Reforms targeting elective backlogs have included independent-sector partnerships and centralised triage hubs, but the impact on emergency department waits has been smaller. Patient advocacy groups argue that any sustainable improvement requires not only more clinicians but also primary care reform that prevents avoidable acute presentations.`,
    qs: [
      { q: 'NHS funding has increased in real terms despite rising demand.', a: 'True', why: '"Total funding has increased in real terms" is stated directly.' },
      { q: 'Independent-sector partnerships significantly reduced emergency department waiting times.', a: 'False', why: '"The impact on emergency department waits has been smaller" — the opposite is stated.' },
      { q: 'Trust-level leaders most frequently cite workforce shortages as the cause of long waits.', a: 'True', why: '"Workforce shortages … are the most cited cause by trust-level leaders."' },
      { q: 'Patient advocacy groups believe that hiring more clinicians alone will create sustainable improvement.', a: 'False', why: 'They argue for "not only more clinicians but also primary care reform" — both are required.' },
    ]
  },
  {
    title: 'Antibiotic resistance',
    text: `Antibiotic resistance is one of the most pressing global health threats of the twenty-first century. Bacteria evolve rapidly under selection pressure, and the overuse of broad-spectrum antibiotics in both human medicine and agriculture has accelerated the emergence of resistant strains. Once-routine infections — urinary tract, post-surgical, respiratory — are returning to the threshold of untreatable. Antimicrobial stewardship programmes encourage clinicians to narrow prescriptions, shorten courses and confirm bacterial cause where possible. Public-facing campaigns, by contrast, focus on patient expectation: a sore throat does not need an antibiotic, but the doctor needs the patient to accept that.`,
    qs: [
      { q: 'Overuse of antibiotics in agriculture has contributed to antibiotic resistance.', a: 'True', why: '"Overuse of broad-spectrum antibiotics in both human medicine and agriculture has accelerated the emergence of resistant strains."' },
      { q: 'Antimicrobial stewardship programmes advise clinicians to use broader antibiotic prescriptions.', a: 'False', why: 'Stewardship programmes "encourage clinicians to narrow prescriptions" — the opposite.' },
      { q: 'Urinary tract infections caused by resistant bacteria are now completely untreatable.', a: "Can't tell", why: '"Returning to the threshold of untreatable" — they are not yet untreatable, and the passage doesn\'t confirm they fully are.' },
      { q: 'Public-facing campaigns aim to change patient expectations about needing antibiotics for sore throats.', a: 'True', why: '"A sore throat does not need an antibiotic, but the doctor needs the patient to accept that" — that is the goal of the campaigns.' },
    ]
  },
  {
    title: 'Informed consent',
    text: `Informed consent is a cornerstone of medical ethics and law. For consent to be valid, the patient must have capacity, be given sufficient information, and make the decision voluntarily. The landmark Montgomery v Lanarkshire ruling in 2015 shifted UK consent law from a doctor-centred standard — what the reasonable doctor would disclose — to a patient-centred one: what the reasonable patient in this individual's position would want to know. This places a higher duty on clinicians to discuss not only common risks but also any risk that a particular patient would regard as significant. The standard applies equally to dental procedures, surgical interventions and non-emergency treatments.`,
    qs: [
      { q: 'The Montgomery ruling made UK consent law more focused on what an individual patient would want to know.', a: 'True', why: 'The ruling shifted to "what the reasonable patient in this individual\'s position would want to know."' },
      { q: 'Before Montgomery, UK consent law required clinicians to disclose all possible risks.', a: 'False', why: 'Before Montgomery the standard was "what the reasonable doctor would disclose" — a doctor-centred, not exhaustive, standard.' },
      { q: 'Consent law as described applies only to surgical procedures.', a: 'False', why: '"The standard applies equally to dental procedures, surgical interventions and non-emergency treatments."' },
      { q: 'Voluntary decision-making is one of the three requirements for valid consent named in the passage.', a: 'True', why: '"Capacity, sufficient information, and voluntary decision" — three requirements are listed.' },
    ]
  }
];

const WINDOWS = [10, 20, 30];
const OPTIONS = ['True', 'False', "Can't tell"];

export default function DrillMemory() {
  const nav = useNavigate();
  const [stage, setStage] = useState('brief');
  const [passageIdx, setPassageIdx] = useState(0);
  const [window, setWindow] = useState(20);
  const [timeLeft, setTimeLeft] = useState(20);
  const [qIdx, setQIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [answers, setAnswers] = useState([]);
  const timerRef = useRef(null);

  const passage = PASSAGES[passageIdx];
  const q = passage.qs[qIdx];
  const correct = answers.filter((a) => a.correct).length;

  useEffect(() => {
    if (stage !== 'reading') return;
    setTimeLeft(window);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          setQIdx(0);
          setAnswers([]);
          setPicked(null);
          setStage('questions');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage, window]);

  function startReading() {
    setStage('reading');
  }

  function submitAnswer() {
    if (!picked) return;
    const isCorrect = picked === q.a;
    const updated = [...answers, { ...q, given: picked, correct: isCorrect }];
    setAnswers(updated);
    setPicked(null);
    if (qIdx < passage.qs.length - 1) {
      setQIdx(qIdx + 1);
    } else {
      setStage('done');
    }
  }

  function restart() {
    setQIdx(0);
    setAnswers([]);
    setPicked(null);
    setStage('brief');
  }

  function exit() {
    nav('/reading-lab');
  }

  const pct = answers.length > 0 ? Math.round((correct / passage.qs.length) * 100) : 0;

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid var(--border)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={exit} className="back-link" style={{ marginBottom: 0 }}>
          <ArrowLeft width="16" height="16" />Exit drill
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="28" height="28" onLight />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi</span>
            <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 3 }}>powered by Medic Mind</span>
          </div>
        </div>
        <div style={{ minWidth: 80, textAlign: 'right', color: 'var(--text-muted)', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
          {stage === 'reading' && <span style={{ color: timeLeft <= 5 ? '#b91c1c' : 'inherit', fontWeight: timeLeft <= 5 ? 700 : 400 }}>{timeLeft}s</span>}
          {stage === 'questions' && `Q${qIdx + 1} / ${passage.qs.length}`}
        </div>
      </header>

      <main className="main drill-shell">
        {stage === 'brief' && (
          <>
            <div className="page-head">
              <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>
                <Brain width="14" height="14" style={{ verticalAlign: 'middle', marginRight: 6 }} />Speed Reader · Drill 03
              </div>
              <h1>Memory trainer</h1>
              <p className="subtitle">The passage is shown for a fixed window, then disappears. Answer four UCAT-style True / False / Can't tell questions entirely from memory. Sharpens the retention you need when passages vanish mid-question on the real test.</p>
            </div>

            <div className="card" style={{ marginBottom: 18 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>Settings</h3>
              <Chips label="Reading window" hint="Passage hides when the clock hits zero"
                options={WINDOWS.map((w) => `${w}s`)} value={`${window}s`}
                onChange={(v) => setWindow(Number(v.replace('s', '')))} />
              <Chips label="Passage"
                options={PASSAGES.map((p) => p.title)} value={passage.title}
                onChange={(v) => setPassageIdx(PASSAGES.findIndex((p) => p.title === v))} />
            </div>

            <button onClick={startReading} className="btn btn-primary" style={{ fontSize: 16, padding: '14px 24px' }}>
              Start reading <ArrowRight width="18" height="18" />
            </button>
          </>
        )}

        {stage === 'reading' && (
          <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Read and remember</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: timeLeft <= 5 ? '#b91c1c' : 'var(--brand-purple)', fontVariantNumeric: 'tabular-nums' }}>{timeLeft}s</span>
              </div>
              <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99 }}>
                <div style={{ width: `${(timeLeft / window) * 100}%`, height: '100%', background: timeLeft <= 5 ? '#ef4444' : 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', borderRadius: 99, transition: 'width 1s linear' }} />
              </div>
            </div>
            <div className="card">
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{passage.title}</div>
              <p style={{ fontSize: 16, lineHeight: 1.75, color: 'var(--text)', margin: 0 }}>{passage.text}</p>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12, textAlign: 'center' }}>Questions appear automatically when time is up.</p>
          </div>
        )}

        {stage === 'questions' && q && (
          <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 20px' }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600 }}>
              Question {qIdx + 1} of {passage.qs.length} · from memory
            </div>
            <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, marginBottom: 20 }}>
              <div style={{ width: `${((qIdx + 1) / passage.qs.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', borderRadius: 99 }} />
            </div>

            <div className="card" style={{ marginBottom: 16, background: 'var(--card-soft)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Statement</div>
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, fontWeight: 500 }}>{q.q}</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              {OPTIONS.map((opt) => (
                <button key={opt} onClick={() => setPicked(opt)} style={{
                  padding: '14px 18px', textAlign: 'left', fontSize: 15, fontWeight: 600, cursor: 'pointer', borderRadius: 10,
                  border: `2px solid ${picked === opt ? 'var(--brand-purple)' : 'var(--border)'}`,
                  background: picked === opt ? 'var(--accent-soft)' : '#fff',
                  color: 'var(--text)',
                }}>{opt}</button>
              ))}
            </div>

            <button onClick={submitAnswer} disabled={!picked} className="btn btn-primary" style={{ opacity: picked ? 1 : 0.5 }}>
              {qIdx === passage.qs.length - 1 ? 'Finish' : 'Next'} <ArrowRight width="14" height="14" />
            </button>
          </div>
        )}

        {stage === 'done' && (
          <>
            <div className="page-head">
              <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Drill complete</div>
              <h1>{correct === passage.qs.length ? 'Perfect recall.' : correct >= 3 ? 'Strong memory.' : 'Keep training.'}</h1>
              <p className="subtitle">"{passage.title}" · {window}s window</p>
            </div>

            <div className="drill-summary">
              <div className="stat"><div className="v">{correct} / {passage.qs.length}</div><div className="l">Correct</div></div>
              <div className="stat"><div className="v">{pct}%</div><div className="l">Accuracy</div></div>
              <div className="stat"><div className="v">{window}s</div><div className="l">Read window</div></div>
            </div>

            <DrillResultBar slug="memory-trainer" score={correct} maxScore={passage.qs.length} accuracyPct={pct} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18, marginBottom: 24 }}>
              {answers.map((a, i) => (
                <div key={i} className="card" style={{ borderLeft: `4px solid ${a.correct ? 'var(--live-green)' : '#ef4444'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {a.correct
                      ? <Check width="16" height="16" style={{ color: 'var(--live-green)', flexShrink: 0 }} />
                      : <X width="16" height="16" style={{ color: '#ef4444', flexShrink: 0 }} />}
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Q{i + 1}</span>
                  </div>
                  <p style={{ margin: '0 0 6px', fontSize: 14 }}>{a.q}</p>
                  <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                    Your answer: <strong style={{ color: a.correct ? 'var(--live-green)' : '#ef4444' }}>{a.given}</strong>
                    {' · '}Correct: <strong>{a.a}</strong>
                  </div>
                  {!a.correct && <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-2)', padding: '6px 10px', background: 'var(--card-soft)', borderRadius: 6 }}>{a.why}</p>}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={restart} className="btn btn-primary">Try again <ArrowRight width="16" height="16" /></button>
              <Link to="/reading-lab" className="btn btn-secondary">Back to Reading Lab</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Chips({ label, hint, options, value, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>{label}</strong>
        {hint && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{hint}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {options.map((opt) => (
          <button key={opt} onClick={() => onChange(opt)} style={{
            padding: '8px 14px', borderRadius: 999, fontSize: 14,
            border: `1px solid ${opt === value ? 'var(--brand-purple)' : 'var(--border)'}`,
            background: opt === value ? 'var(--accent-soft)' : '#fff',
            color: opt === value ? 'var(--brand-deep)' : 'var(--text-2)',
            fontWeight: opt === value ? 600 : 500,
          }}>{opt}</button>
        ))}
      </div>
    </div>
  );
}
